import { Link } from 'react-router-dom';

import { useAuth } from '@/features/auth';
import { AppHeader, HeaderUserActions } from '@/shared/components/layout';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';

import { DOCS_SHELL } from './docsShell.constant';
import { DocsSearchTrigger } from './DocsSearchTrigger';

/** 관리 진입점을 보여줄 권한. 서버도 같은 기준(ADMIN 이상)으로 막는다. */
const MANAGER_ROLES = ['ADMIN', 'SUPER_ADMIN'];

interface DocsHeaderProps {
  /** ⌘K 다이얼로그 열기. */
  onSearch: () => void;
  /** 모바일 사이드바 드로어 토글. */
  drawerOpen: boolean;
  onToggleDrawer: () => void;
}

/**
 * 문서 상세 전용 헤더.
 *
 * 마케팅 헤더를 그대로 쓰지 않는 이유는 메가메뉴다. 문서를 읽는 중에 마우스가
 * 위로 스치면 화면 절반이 메뉴로 덮이고, 그 아래 sticky 사이드바·목차와 겹친다.
 * 여기서는 **문서 안에서 필요한 것**(검색 · 문서 목록 · 관리)만 남긴다.
 *
 * 껍데기(높이·배경·로고)는 `AppHeader` 가, 오른쪽 사용자 영역은
 * `HeaderUserActions` 가 마케팅 헤더와 공유한다 — 두 헤더 사이를 오갈 때
 * 로고와 아바타가 같은 자리에 있어야 다른 사이트로 넘어온 느낌이 안 난다.
 *
 * 바는 **뷰포트 끝까지** 쓰고 좌측 존을 사이드바 폭에 맞춘다(`DOCS_SHELL`).
 * 그래야 로고가 사이드바 위에, 검색창이 본문 열 위에 선다 — 헤더만 가운데로
 * 모으면 넓은 화면에서 로고가 사이드바와 어긋난 채 허공에 뜬다.
 */
export function DocsHeader({
  onSearch,
  drawerOpen,
  onToggleDrawer,
}: DocsHeaderProps) {
  const { user } = useAuth();
  const canManage = !!user?.userRole && MANAGER_ROLES.includes(user.userRole);

  return (
    <AppHeader
      position="sticky"
      width="full"
      startWidth={DOCS_SHELL.headerStart}
      start={
        <>
          <Link
            to={PATHS.DOCS}
            className="hidden shrink-0 text-[18px] font-semibold text-neutral-950/70 transition-colors hover:text-neutral-950 sm:inline dark:text-white/70 dark:hover:text-white"
          >
            가이드
          </Link>

          {/* 모바일 — 사이드바가 드로어로 접히므로 여는 버튼이 헤더에 있어야 한다. */}
          <button
            type="button"
            onClick={onToggleDrawer}
            aria-expanded={drawerOpen}
            aria-controls="docs-sidebar-drawer"
            className="rounded-md px-2 py-1.5 text-[14px] font-semibold text-neutral-950/70 transition-colors hover:bg-black/[0.04] lg:hidden dark:text-white/70 dark:hover:bg-white/10"
          >
            {drawerOpen ? '✕ 닫기' : '☰ 문서'}
          </button>
        </>
      }
      center={
        // 본문 열 왼쪽에 붙되 무한정 늘어나지는 않는다 — 2000px 짜리 검색
        // '버튼' 은 ⌘K 배지만 저 멀리 떠 있는 이상한 모양이 된다.
        <div className="max-w-md">
          <DocsSearchTrigger onClick={onSearch} />
        </div>
      }
      end={
        <>
          {/* 폭이 좁아 검색창이 빠지면 아이콘으로 대체한다 — 검색은 도움말의 주 진입로다. */}
          <button
            type="button"
            onClick={onSearch}
            aria-label="문서 검색"
            className="grid h-10 w-10 place-items-center rounded-full text-lg text-neutral-950/60 transition-colors hover:bg-black/[0.05] lg:hidden dark:text-white/60 dark:hover:bg-white/10"
          >
            ⌕
          </button>
          {canManage && (
            <Link
              to={PATHS.DOCS_MANAGE}
              className="hidden rounded-lg border border-brand-200 px-3 py-1.5 text-[14px] font-bold text-brand-700 transition-colors hover:bg-brand-50 sm:inline-block dark:border-brand-500/30 dark:text-brand-300 dark:hover:bg-brand-500/10"
            >
              관리
            </Link>
          )}
          {/* 알림은 문서를 읽는 흐름과 관계가 없다 — 종만 빼고 나머지는 공통. */}
          <HeaderUserActions showNotifications={false} />
        </>
      }
    />
  );
}
